import React from "react";
import { Typography, Button } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import DropEpisodesSection from "./drop-episodes-section";
import CommentAssigneeSection from "./comment-assignee-section";
import SleepSpo2HrChart from "./sleep-spo2-hr-chart";
import axios from "../../../../../utils/lib/axios";
// import PhysicianInterpretationSection from "./physician-interpretation-section";

const titleStyle = {
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: 600,
    color: "#313131",
    textAlign: "center",
    marginBottom: "4px",
    width: "100%",
};

const labelStyle = {
    fontFamily: "Poppins",
    fontSize: "16px",
    fontWeight: 400,
    color: "#444444",
    marginBottom: "4px",
};

const valueStyle = {
    fontFamily: "Poppins",
    fontSize: "16px",
    color: "#444444",
    lineHeight: 1.4,
};

const valueBoldStyle = {
    ...valueStyle,
    fontWeight: 700,
    fontSize: "18px",
};

const durationLineStyle = {
    fontFamily: "Poppins",
    fontSize: "16px",
    color: "#444444",
};

const iconCircleStyle = {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#444444",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
};

const cardStyle = {
    display: "flex",
    flexDirection: "row",
    gap: "32px",
    flexWrap: "wrap",
    justifyContent: "center",
    padding: "20px 24px",
    backgroundColor: "#FAFBFE",
    borderRadius: "15px",
    border: "1px solid #F0F2F9",
};

const timeColumnStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
    minWidth: "220px",
};

const timeColumnTextStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
};

const metricsBlockStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignSelf: "stretch",
    alignItems: "center",
};

const metricLineStyle = {
    fontFamily: "Poppins",
    fontSize: "16px",
    color: "#444444",
    textAlign: "center",
};

const containerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    alignSelf: "center",
    width: "100%",
};

// const inputBlockStyle = {
//     display: "flex",
//     flexDirection: "column",
//     gap: "8px",
//     minWidth: "200px",
//     flex: 1,
// };

// const readOnlyCommentStyle = {
//     ...labelStyle,
//     fontFamily: "Poppins",
//     fontSize: "13px",
//     color: "#333",
//     lineHeight: 1.5,
//     padding: "8px 10px",
//     backgroundColor: "#F5F5F5",
//     borderRadius: "4px",
//     border: "1px solid #E8E8E8",
//     minHeight: "56px",
//     whiteSpace: "pre-wrap",
// };

const validationSchema = Yup.object().shape({
    techComments: Yup.string()
        .required("Technologist Comments is required")
        .max(1000, "Technologist Comments must be at most 1000 characters"),
    assignedTo: Yup.string().required("Assign to is required"),
    // physicianInterpretation: Yup.string()
    //     .required("Physician Interpretation is required")
    //     .max(1000, "Physician Interpretation must be at most 1000 characters"),
});

/**
 * Displays sleep summary to match Figma: title, duration (X hr Y min Z sec),
 * Start/End time card with icons, and other metrics (ODI Total, O₂ Low, T85, T90).
 */
export default function SleepSummarySection({
    deviceName,
    durationHours = 0,
    durationMinutes = 0,
    durationSeconds = 0,
    startValue,
    endValue,
    odiTotal = 10,
    o2Low = 11,
    t85 = 11,
    t90 = 11,

    assignTo: assignToProp,
    onAssignToChange,
    comment: commentProp,

    physicianComment: physicianCommentProp,

    dropEpisodes = [],

    adminList = [],
    reportData = {},
    setAlertDetails,
    reportId,
    setReportGeneratedSuccessAlert,
    setSleepReportModalOpen,
    loadWaitingData,
    onSleepReportGenerated,
    onBack,
}) {
    const commentAssigneeSectionRef = React.useRef(null);

    const scrollToCommentAssigneeSection = () => {
        window.requestAnimationFrame(() => {
            commentAssigneeSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    };

    const parsedDuration = (() => {
        const src = reportData?.sleep_duration ?? reportData?.sleepDuration;
        if (!src) return null;

        // Support object shape: { hours, minutes, seconds } or { total_seconds }
        if (typeof src === "object") {
            const h = Number(src.hours ?? src.h);
            const m = Number(src.minutes ?? src.m);
            const s = Number(src.seconds ?? src.s);
            if (!Number.isNaN(h) && !Number.isNaN(m) && !Number.isNaN(s)) {
                return { hours: h, minutes: m, seconds: s };
            }
            const totalSeconds = Number(
                src.total_seconds ?? src.totalSeconds ?? src.total_seconds_sec,
            );
            if (!Number.isNaN(totalSeconds) && totalSeconds >= 0) {
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                return { hours, minutes, seconds };
            }
            return null;
        }

        // Support number shape: treat as seconds
        if (typeof src === "number") {
            const totalSeconds = src;
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            return { hours, minutes, seconds };
        }

        // Support string shape
        if (typeof src === "string") {
            const str = src.trim();

            // "X hr Y min Z sec"
            const hrMatch = str.match(/(\d+)\s*hr/i);
            const minMatch = str.match(/(\d+)\s*min/i);
            const secMatch = str.match(/(\d+)\s*sec/i);
            if (hrMatch && minMatch && secMatch) {
                return {
                    hours: Number(hrMatch[1]),
                    minutes: Number(minMatch[1]),
                    seconds: Number(secMatch[1]),
                };
            }

            // "HH:MM:SS"
            if (str.includes(":")) {
                const parts = str.split(":").map((p) => Number(p));
                if (parts.length >= 3 && parts.every((n) => !Number.isNaN(n))) {
                    return {
                        hours: parts[0],
                        minutes: parts[1],
                        seconds: parts[2],
                    };
                }
            }

            // "H.M.S"
            if (str.includes(".")) {
                const parts = str.split(".").map((p) => Number(p));
                if (parts.length >= 3 && parts.every((n) => !Number.isNaN(n))) {
                    return {
                        hours: parts[0],
                        minutes: parts[1],
                        seconds: parts[2],
                    };
                }
            }

            // Plain numeric string => seconds
            if (/^\d+$/.test(str)) {
                const totalSeconds = Number(str);
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                return { hours, minutes, seconds };
            }
        }

        return null;
    })();

    const durationForDisplay = parsedDuration ?? {
        hours: durationHours,
        minutes: durationMinutes,
        seconds: durationSeconds,
    };

    // Backend-provided display strings for sleep start/end
    // (e.g. start_time_display: "10:00:00 PM", start_date_display: "Mar 07, 2026")
    const startDisplayValue =
        reportData?.start_time_display && reportData?.start_date_display
            ? `${reportData.start_time_display} ${reportData.start_date_display}`
            : startValue;
    const endDisplayValue =
        reportData?.end_time_display && reportData?.end_date_display
            ? `${reportData.end_time_display} ${reportData.end_date_display}`
            : endValue;

    // These are displayed as-is (UI already appends % where needed).
    const odiTotalForDisplay = reportData?.odi_per_hour ?? odiTotal;
    const o2LowForDisplay = reportData?.o2_low ?? o2Low;
    const t85ForDisplay = reportData?.t1_value ?? t85;
    const t90ForDisplay = reportData?.t2_value ?? t90;
    const t1LabelForDisplay = reportData?.t1_label ?? "T85";
    const t2LabelForDisplay = reportData?.t2_label ?? "T90";

    const generateReport = async (values) => {
        try {
            const response = await axios.patch(
                "/sleep/update-report",

                {
                    report_id: reportId,
                    technologist_comments: values.techComments,
                    assign_to: values.assignedTo,
                },
            );

            loadWaitingData?.();
            onSleepReportGenerated?.();
            setSleepReportModalOpen?.(false);

            //TODO: Need to do functionality implementation
            setReportGeneratedSuccessAlert?.({
                showAlert: true,
                alertDetails: {
                    ...response.data,
                    report_pdf: response.data?.pdf_url ?? response.data?.report_pdf,
                },
            });
        } catch (error) {
            setReportGeneratedSuccessAlert?.({
                showAlert: false,
                alertDetails: {},
            });
            setAlertDetails?.({
                message:
                    error?.response?.data?.message ||
                    "Something went wrong while verifying sleep data. Please try again.",
                severity: "error",
                showAlert: true,
            });
        }
    };

    return (
        <Formik
            initialValues={{
                techComments: "",
                assignedTo: "",
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => generateReport(values)}
        >
            {({ isSubmitting, validateForm, setTouched, submitForm }) => (
                <Form>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignSelf: "stretch",
                            alignItems: "flex-start",
                            gap: "24px",
                            padding: "20px",
                        }}
                    >
                        <Typography style={titleStyle}>
                            Sleep Summary – Device:{" "}
                            {reportData?.device_name ?? deviceName}
                        </Typography>

                        {/* Sleep Duration: "7 hr 15 min 2 sec" with bold numbers – same width as card */}
                        <div
                            style={{
                                alignSelf: "center",
                                width: "fit-content",
                            }}
                        >
                            <Typography
                                component="div"
                                style={durationLineStyle}
                            >
                                Sleep Duration:{" "}
                                <span style={{ fontWeight: 700 }}>
                                    {durationForDisplay.hours}
                                </span>{" "}
                                hr{" "}
                                <span style={{ fontWeight: 700 }}>
                                    {durationForDisplay.minutes}
                                </span>{" "}
                                min{" "}
                                <span style={{ fontWeight: 700 }}>
                                    {durationForDisplay.seconds}
                                </span>{" "}
                                sec
                            </Typography>
                        </div>

                        {/* Start Time & End Time card with icons and vertical separator */}
                        <div
                            style={{
                                ...cardStyle,
                                alignSelf: "center",
                                width: "fit-content",
                            }}
                        >
                            <div style={timeColumnStyle}>
                                <div style={iconCircleStyle}>
                                    <BedtimeIcon sx={{ fontSize: 22 }} />
                                </div>
                                <div style={timeColumnTextStyle}>
                                    <Typography style={labelStyle}>
                                        Start Time
                                    </Typography>
                                    <Typography style={valueBoldStyle}>
                                        {startDisplayValue}
                                    </Typography>
                                </div>
                            </div>
                            <div
                                style={{
                                    width: "1px",
                                    backgroundColor: "#E0E0E0",
                                    alignSelf: "stretch",
                                    minHeight: "48px",
                                }}
                            />
                            <div style={timeColumnStyle}>
                                <div style={iconCircleStyle}>
                                    <WbSunnyIcon sx={{ fontSize: 22 }} />
                                </div>
                                <div style={timeColumnTextStyle}>
                                    <Typography style={labelStyle}>
                                        End Time
                                    </Typography>
                                    <Typography style={valueBoldStyle}>
                                        {endDisplayValue}
                                    </Typography>
                                </div>
                            </div>
                        </div>

                        {/* Other metrics: ODI Total, O₂ Low, T85, T90 */}
                        <div style={metricsBlockStyle}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "24px",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography style={metricLineStyle}>
                                    ODI Total:{" "}
                                    <span style={{ fontWeight: 700 }}>
                                        {odiTotalForDisplay}
                                    </span>{" "}
                                    /hour
                                </Typography>
                                <Typography style={metricLineStyle}>
                                    O₂ Low:{" "}
                                    <span style={{ fontWeight: 700 }}>
                                        {o2LowForDisplay}
                                    </span>
                                    %
                                </Typography>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "24px",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography style={metricLineStyle}>
                                    {t1LabelForDisplay}:{" "}
                                    <span style={{ fontWeight: 700 }}>
                                        {t85ForDisplay}
                                    </span>
                                    %
                                </Typography>
                                <Typography style={metricLineStyle}>
                                    {t2LabelForDisplay}:{" "}
                                    <span style={{ fontWeight: 700 }}>
                                        {t90ForDisplay}
                                    </span>
                                    %
                                </Typography>
                            </div>
                        </div>

                        {/* SpO₂ and Pulse Rate chart – image from backend or default */}
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "100%",
                                marginTop: "8px",
                            }}
                        >
                            <SleepSpo2HrChart
                                reportData={reportData.trend}
                                dropEpisodes={
                                    dropEpisodes != null ? dropEpisodes : []
                                }
                            />
                        </div>

                        <div style={containerStyle}>
                            {/* <div
                                style={{
                                    ...inputBlockStyle,
                                    flex: "1 1 100%",
                                    gap: "4px",
                                    maxWidth: "100%",
                                }}
                            >
                                <Typography style={labelStyle}>
                                    Technologist Comments
                                </Typography>

                                <Typography
                                    component="div"
                                    style={readOnlyCommentStyle}
                                >
                                    This is the Technologist comment
                                </Typography>
                            </div> */}

                            <div
                                ref={commentAssigneeSectionRef}
                                id="sleep-summary-comment-assignee"
                                style={{ width: "100%" }}
                            >
                                <CommentAssigneeSection
                                    commentName="techComments"
                                    assignToName="assignedTo"
                                    assignToOptions={adminList}
                                />
                            </div>
                        </div>

                        <DropEpisodesSection
                            episodes={reportData.drop_episodes}
                        />
                        {/* <div style={containerStyle}>
                            <div
                                style={{
                                    ...inputBlockStyle,
                                    flex: "1 1 100%",
                                    gap: "4px",
                                    maxWidth: "100%",
                                }}
                            >
                                <Typography style={labelStyle}>
                                    Physician Interpretation:
                                </Typography>

                                <Typography
                                    component="div"
                                    style={readOnlyCommentStyle}
                                >
                                    This is the Physician Interpretation
                                </Typography>
                            </div>
                            <PhysicianInterpretationSection
                                value={physicianCommentProp ?? ""}
                                onChange={onPhysicianCommentChange}
                                error={physicianCommentError}
                            />
                        </div> */}
                        {/* <div
                            style={{
                                alignSelf: "stretch",
                                marginTop: "24px",
                                paddingTop: "20px",
                                borderTop: "1px solid #E0E0E0",
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                style={{
                                    fontFamily: "Poppins",
                                    fontSize: "13px",
                                    color: "#444",
                                }}
                            >
                                Electronically signed by ................
                            </Typography>
                            <Typography
                                style={{
                                    fontFamily: "Poppins",
                                    fontSize: "13px",
                                    color: "#444",
                                }}
                            >
                                Date and time ................
                            </Typography>
                        </div> */}
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "12px",
                            marginTop: "auto",
                            paddingTop: "24px",
                            alignSelf: "stretch",
                            width: "100%",
                        }}
                    >
                        <Button
                            onClick={onBack}
                            variant="outlined"
                            type="button"
                            disabled={isSubmitting}
                            style={{
                                color: "#197F8F",
                                padding: "8px 24px",
                                border: "1px solid #197F8F",
                                borderRadius: "8px",
                                textTransform: "none",
                                fontFamily: "Poppins",
                                fontWeight: 500,
                                fontSize: "16px",
                                width: "196px",
                                height: "48px",
                            }}
                        >
                            Back
                        </Button>

                        <Button
                            type="button"
                            disabled={isSubmitting}
                            onClick={async () => {
                                const validationErrors = await validateForm();
                                const hasErrors =
                                    Object.keys(validationErrors).length > 0;
                                if (hasErrors) {
                                    setTouched(
                                        {
                                            techComments: true,
                                            assignedTo: true,
                                        },
                                        true,
                                    );
                                    setTimeout(scrollToCommentAssigneeSection, 0);
                                    return;
                                }
                                submitForm();
                            }}
                            style={{
                                backgroundColor: "#197F8F",
                                color: "white",
                                padding: "8px 24px",
                                border: "1px solid #197F8F",
                                borderRadius: "8px",
                                textTransform: "none",
                                fontFamily: "Poppins",
                                fontWeight: 500,
                                fontSize: "16px",
                                width: "196px",
                                height: "48px",
                                opacity: isSubmitting ? 0.7 : 1,
                            }}
                        >
                            Generate
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
}
